import type {
  CmaListingsByAreaQuery,
  CmaListingsByRadiusQuery,
  ComparableCmaListingsQuery,
  GetPropertyTypesQuery,
} from '@graphql/__generated__/graphql';

export type ListingItem =
  | NonNullable<
      NonNullable<
        ComparableCmaListingsQuery['ch_get_cma_listings_distance']
      >[number]['listing']
    >
  | NonNullable<
      NonNullable<
        CmaListingsByRadiusQuery['ch_get_cma_listings_by_radius']
      >[number]['listing']
    >
  | NonNullable<NonNullable<CmaListingsByAreaQuery['ch_cma_listings']>[number]>;

type PropertyTypes = GetPropertyTypesQuery['property_types'];

export type TransformedPropertyData = {
  property_type_id: string;
  composite_type?: string;
  number_of_rooms?: number;
  number_of_bedrooms?: number;
  living_surface?: number;
  land_surface?: number;
};

export type TransformedListingImage = {
  bucket_name: string;
  file_name: string;
};

export const listingToPropertyData = (
  listing: ListingItem,
  propertyTypes: PropertyTypes | undefined,
): TransformedPropertyData | undefined => {
  if (!listing?.property_type || !propertyTypes) {
    return undefined;
  }

  const pt = propertyTypes.find(
    p => p.name === listing?.property_type && p.type === 'simple',
  );

  if (!pt) {
    return undefined;
  }

  return {
    property_type_id: pt.id,
    composite_type: pt.composite_type ?? pt.main_type ?? undefined,
    number_of_rooms: listing.number_of_rooms ?? undefined,
    number_of_bedrooms: listing.number_of_bedrooms ?? undefined,
    living_surface: listing.computed_surface ?? undefined,
    land_surface: listing.land_surface ?? undefined,
  };
};

export const listingToImage = (
  listing: ListingItem,
): TransformedListingImage | undefined => {
  const img = listing?.images?.[0];
  if (!img?.bucket_name || !img?.file_name) {
    return undefined;
  }

  return {
    bucket_name: img.bucket_name,
    file_name: img.file_name,
  };
};

export type TransformedAddressData = {
  route?: string;
  street_number?: string;
  postcode?: string;
  locality?: string;
  country_code?: string;
  state?: string;
};

export const listingToAddress = (
  listing: ListingItem,
): TransformedAddressData => {
  const result: TransformedAddressData = {};

  if (listing.route) {
    result.route = listing.route;
  }
  if (listing.street_number) {
    result.street_number = listing.street_number;
  }
  if (listing.postcode) {
    result.postcode = listing.postcode;
  }
  if (listing.locality) {
    result.locality = listing.locality;
  }
  if (listing.country_code) {
    result.country_code = listing.country_code;
  }
  if (listing.state?.name) {
    result.state = listing.state.name;
  }

  return result;
};

export const extractListingsFromQuery = (
  listingsData: ComparableCmaListingsQuery | undefined,
): ListingItem[] => {
  if (!listingsData) {
    return [];
  }

  const countries = [
    listingsData.ch_get_cma_listings_distance,
    listingsData.fr_get_cma_listings_distance,
    listingsData.es_get_cma_listings_distance,
    listingsData.it_get_cma_listings_distance,
    listingsData.pl_get_cma_listings_distance,
    listingsData.pt_get_cma_listings_distance,
  ];

  for (const listings of countries) {
    if (listings && listings.length > 0) {
      return listings.map(l => l.listing) as ListingItem[];
    }
  }

  return [];
};

export const extractRadiusListingsFromQuery = (
  data: CmaListingsByRadiusQuery | undefined,
): ListingItem[] => {
  if (!data) {
    return [];
  }

  const countries = [
    data.ch_get_cma_listings_by_radius,
    data.fr_get_cma_listings_by_radius,
    data.es_get_cma_listings_by_radius,
    data.it_get_cma_listings_by_radius,
    data.pl_get_cma_listings_by_radius,
    data.pt_get_cma_listings_by_radius,
  ];

  for (const listings of countries) {
    if (listings && listings.length > 0) {
      return listings.map(l => l.listing) as ListingItem[];
    }
  }

  return [];
};

export const extractAreaListingsFromQuery = (
  data: CmaListingsByAreaQuery | undefined,
): ListingItem[] => {
  if (!data) {
    return [];
  }

  const countries = [
    data.ch_cma_listings,
    data.fr_cma_listings,
    data.es_cma_listings,
    data.it_cma_listings,
    data.pl_cma_listings,
    data.pt_cma_listings,
  ];

  for (const listings of countries) {
    if (listings && listings.length > 0) {
      return listings as unknown as ListingItem[];
    }
  }

  return [];
};
